import type { CSSProperties } from 'react';
import { Toaster as Sonner } from 'sonner';

import { useTheme } from '@/settings/theme';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const ToasterSonner = ({ ...props }: ToasterProps) => {
  const { theme } = useTheme();

  return (
    <Sonner
      // theme={theme as ToasterProps["theme"]}
      theme={theme}
      className="toaster group"
      // SYSTEM toasts stack top-center (design decision). CONVERSATIONAL toasts
      // are not sonner toasts at all — they render as a speech bubble out of the
      // collapsed Professor Ka launcher (bottom-right), see `ka-bubble-store` —
      // and only fall back INTO this stack when Ka isn't available. The typed
      // `flashka-toast` helpers no longer accept `position`, so every DS toast
      // lands here.
      position="top-center"
      // The toast column is the shared DS Toast's `md` MAX width (560px). The
      // card itself hugs its content (`w-fit`) and only reaches 560 with long
      // copy, so this is a ceiling, not the rendered width — RESET_WRAPPER
      // centers the hugging card inside the full-width row. sonner clamps to
      // the viewport on mobile. sonner's default is 356px.
      style={{ '--width': '560px' } as CSSProperties}
      toastOptions={{
        classNames: {
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-lg px-4 py-2 w-fit',
          description: 'group-[.toast]:text-muted-foreground',
          actionButton:
            'group-[.toast]:bg-primary group-[.toast]:text-primary-foreground',
          cancelButton: 'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground',
          closeButton: 'rounded-md bg-surface-raised',
        },
      }}
      richColors
      closeButton
      // NOTE: `expand` is deliberately NOT set — the design calls for a real
      // stack (cards peeking behind the front one, expanding on hover), which is
      // sonner's collapsed default. An earlier iteration DID set `expand`
      // because the collapsed stack reflowed on every enter/leave: it clamps
      // behind-toasts to `--front-toast-height` but only hides their content for
      // its OWN styled toasts (`[data-styled='true'] > * { opacity: 0 }`), and
      // our custom cards are `unstyled`. The real fix is that one missing rule,
      // now in index.css — not flattening the stack. Do not re-add `expand`.
      //
      // Max 3 stacked toasts visible at once (design-system rule); the rest
      // queue behind them. Matches the mobile sonner-native `visibleToasts`.
      visibleToasts={3}
      {...props}
    />
  );
};

export { ToasterSonner };
